import dayjs from "dayjs";
import { ESTADOS_TAREA, TIPOS_TAREA } from "../hooks/useTareasFilters";
import { Box, Button, Chip, Typography } from "@material-ui/core";
import { Clear } from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";

// ==================== ESTILOS ====================

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    backgroundColor: "#F5F5F5",
    borderRadius: 8,
    marginTop: theme.spacing(2),
  },
  label: {
    fontSize: "0.813rem",
    color: "#757575",
    fontWeight: 500,
    marginRight: theme.spacing(0.5),
  },
  chip: {
    backgroundColor: "#FFFFFF",
    border: "1px solid #E0E0E0",
    fontWeight: 500,
    fontSize: "0.75rem",
    height: 28,
    transition: "all 0.15s ease",
    "& .MuiChip-deleteIcon": {
      color: "#9E9E9E",
      fontSize: 16,
      transition: "color 0.15s ease",
      "&:hover": {
        color: "#EF5350",
      },
    },
    "&:hover": {
      backgroundColor: "#FAFAFA",
      borderColor: "#BDBDBD",
    },
  },
  clearButton: {
    marginLeft: "auto",
    color: "#757575",
    fontSize: "0.813rem",
    fontWeight: 500,
    textTransform: "none",
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 6,
    "&:hover": {
      backgroundColor: "#EEEEEE",
      color: "#424242",
    },
  },
}));

/**
 * Componente que muestra los filtros activos como chips removibles
 */
export default function ActiveFiltersChips({
  filtros,
  removerFiltro,
  limpiarFiltros,
  hayFiltrosActivos,
  estadosDisponibles, // opcional — si se omite, usa ESTADOS_TAREA por defecto
}) {
  const classes = useStyles();

  if (!hayFiltrosActivos) return null;

  const formatFecha = (fecha) => {
    if (!fecha) return "";
    return dayjs(fecha).format("DD/MM/YYYY");
  };

  const getEstadoLabel = (estado) => {
    // Busca primero en la lista de estados inyectada por el padre (si existe),
    // luego en los estados de tareas por compatibilidad hacia atrás.
    const found = (estadosDisponibles || ESTADOS_TAREA).find(
      (e) => e.value === estado,
    );
    return found?.label || estado;
  };

  const getTipoTareaLabel = (tipo) => {
    const found = TIPOS_TAREA.find((t) => t.value === tipo);
    return found?.label || tipo;
  };

  return (
    <Box className={classes.container}>
      <Typography className={classes.label}>Filtros activos:</Typography>

      {/* Chip de búsqueda */}
      {filtros.busqueda && (
        <Chip
          size="small"
          label={`Búsqueda: "${filtros.busqueda}"`}
          onDelete={() => removerFiltro("busqueda")}
          className={classes.chip}
        />
      )}

      {/* Chips de estados */}
      {filtros.estados?.map((estado) => (
        <Chip
          key={estado}
          size="small"
          label={getEstadoLabel(estado)}
          onDelete={() => removerFiltro("estados", estado)}
          className={classes.chip}
        />
      ))}

      {/* Chip de tipo tarea */}
      {filtros.tipoTarea && (
        <Chip
          size="small"
          label={`Tipo: ${getTipoTareaLabel(filtros.tipoTarea)}`}
          onDelete={() => removerFiltro("tipoTarea")}
          className={classes.chip}
        />
      )}

      {/* Chips de fechas — facturas */}
      {filtros.fechaFacturacionDesde && (
        <Chip
          size="small"
          label={`Desde: ${formatFecha(filtros.fechaFacturacionDesde)}`}
          onDelete={() => removerFiltro("fechaFacturacionDesde")}
          className={classes.chip}
        />
      )}
      {filtros.fechaFacturacionHasta && (
        <Chip
          size="small"
          label={`Hasta: ${formatFecha(filtros.fechaFacturacionHasta)}`}
          onDelete={() => removerFiltro("fechaFacturacionHasta")}
          className={classes.chip}
        />
      )}
      {filtros.montoMin && (
        <Chip
          size="small"
          label={`Monto mín: $${Number(filtros.montoMin).toLocaleString("es-CL")}`}
          onDelete={() => removerFiltro("montoMin")}
          className={classes.chip}
        />
      )}
      {filtros.montoMax && (
        <Chip
          size="small"
          label={`Monto máx: $${Number(filtros.montoMax).toLocaleString("es-CL")}`}
          onDelete={() => removerFiltro("montoMax")}
          className={classes.chip}
        />
      )}

      {/* Chips de fechas — tareas */}
      {filtros.fechaInicioDesde && (
        <Chip
          size="small"
          label={`Inicio desde: ${formatFecha(filtros.fechaInicioDesde)}`}
          onDelete={() => removerFiltro("fechaInicioDesde")}
          className={classes.chip}
        />
      )}
      {filtros.fechaInicioHasta && (
        <Chip
          size="small"
          label={`Inicio hasta: ${formatFecha(filtros.fechaInicioHasta)}`}
          onDelete={() => removerFiltro("fechaInicioHasta")}
          className={classes.chip}
        />
      )}

      {/* Chips de fechas término */}
      {filtros.fechaTerminoDesde && (
        <Chip
          size="small"
          label={`Término desde: ${formatFecha(filtros.fechaTerminoDesde)}`}
          onDelete={() => removerFiltro("fechaTerminoDesde")}
          className={classes.chip}
        />
      )}
      {filtros.fechaTerminoHasta && (
        <Chip
          size="small"
          label={`Término hasta: ${formatFecha(filtros.fechaTerminoHasta)}`}
          onDelete={() => removerFiltro("fechaTerminoHasta")}
          className={classes.chip}
        />
      )}

      {/* Chip de cliente */}
      {filtros.cliente && (
        <Chip
          size="small"
          label={`Cliente: ${filtros.cliente.label}`}
          onDelete={() => removerFiltro("cliente")}
          className={classes.chip}
        />
      )}

      {/* Chip de empleado */}
      {filtros.empleado && (
        <Chip
          size="small"
          label={`Empleado: ${filtros.empleado.label}`}
          onDelete={() => removerFiltro("empleado")}
          className={classes.chip}
        />
      )}

      {/* Chips de sucursales seleccionadas */}
      {filtros.sucursales?.map((sucursal) => (
        <Chip
          key={sucursal}
          size="small"
          label={`Sucursal: ${sucursal}`}
          onDelete={() => removerFiltro("sucursales", sucursal)}
          className={classes.chip}
        />
      ))}

      {/* Botón limpiar todos */}
      <Button
        size="small"
        startIcon={<Clear sx={{ fontSize: 16 }} />}
        onClick={limpiarFiltros}
        className={classes.clearButton}
      >
        Limpiar todos
      </Button>
    </Box>
  );
}
